import React, { useEffect, useRef, useState, useMemo } from 'react';

/**
 * WebFrameRenderer 컴포넌트
 * 웹페이지를 iframe으로 렌더링하거나 이미지를 img 태그로 렌더링하는 차트 컴포넌트
 * 콘텐츠 타입(이미지, 영상, 웹페이지)에 따라 자동으로 최적 사이즈 조정
 * 
 * chartData 구조:
 * {
 *   "viz_type": "webframe",
 *   "data": {
 *     "url": "https://example.com" 또는 "https://example.com/image.jpg",
 *     "allow": "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
 *   },
 *   "options": {
 *     "title": "웹페이지 제목",
 *     "height": "600px",
 *     "showBorder": true,
 *     "borderColor": "#e0e0e0",
 *     "borderRadius": "8px",
 *     "sandbox": "allow-scripts allow-same-origin",
 *     "loading": "lazy",
 *     "objectFit": "contain", // 이미지용: "contain", "cover", "fill", "scale-down"
 *     "autoSizeAdjust": true // 자동 사이즈 조정 활성화 (기본값: true)
 *   }
 * }
 */

// 콘텐츠 타입 감지 함수들
const isImageUrl = (url) => {
  if (!url) return false;
  
  // URL에서 쿼리 파라미터 제거하고 확장자 확인
  const cleanUrl = url.split('?')[0].toLowerCase();
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico', '.tiff', '.tif'];
  
  return imageExtensions.some(ext => cleanUrl.endsWith(ext));
};

const isVideoStreamUrl = (url) => {
  if (!url) return false;
  
  const lowerUrl = url.toLowerCase();
  
  // 스트리밍 프로토콜 또는 패턴 감지 (더 정확한 패턴)
  const streamPatterns = [
    '/camera/', '/stream/', '/live/', '/video/', '/cam/',
    'rtsp://', 'rtmp://', 'hls://', 'rtmp://',
    'camera', 'webcam', 'nvr', 'dvr', 'cctv',
    'mjpeg', 'h264', 'h265',
    ':8080', ':8081', ':8554',  // 일반적인 스트리밍 포트
  ];
  
  const isStream = streamPatterns.some(pattern => lowerUrl.includes(pattern));
  console.log('[WebFrameRenderer] URL 영상 감지:', { 
    url, 
    isStream, 
    matchedPatterns: streamPatterns.filter(p => lowerUrl.includes(p)) 
  });
  
  return isStream;
};

const getContentType = (url) => {
  if (isImageUrl(url)) return 'image';
  if (isVideoStreamUrl(url)) return 'video';
  return 'webpage';
};

// 영상 URL로부터 예상 비율 추정
const estimateVideoAspectRatio = (url) => {
  if (!url) return '16/9';
  
  const lowerUrl = url.toLowerCase();
  let reason = '기본값'; // 어떤 조건으로 비율이 결정되었는지 추적
  
  // 해상도별 비율 추정 (가장 높은 우선순위)
  if (lowerUrl.includes('1920x1080') || lowerUrl.includes('1080p')) {
    reason = '1080p 해상도'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
    return '16/9';
  }
  if (lowerUrl.includes('1280x720') || lowerUrl.includes('720p')) {
    reason = '720p 해상도'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
    return '16/9';
  }
  if (lowerUrl.includes('640x480') || lowerUrl.includes('480p')) {
    reason = '480p 해상도'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '4/3', reason });
    return '4/3';
  }
  if (lowerUrl.includes('1024x768')) {
    reason = '1024x768 해상도'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '4/3', reason });
    return '4/3';
  }
  if (lowerUrl.includes('800x600')) {
    reason = '800x600 해상도'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '4/3', reason });
    return '4/3';
  }
  if (lowerUrl.includes('1280x1024')) {
    reason = '1280x1024 해상도'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '5/4', reason });
    return '5/4';
  }
  if (lowerUrl.includes('1600x1200')) {
    reason = '1600x1200 해상도'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '4/3', reason });
    return '4/3';
  }
  if (lowerUrl.includes('ultrawide') || lowerUrl.includes('21:9')) {
    reason = 'ultrawide 키워드'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '21/9', reason });
    return '21/9';
  }
  
  // fullscreen 키워드가 있으면 16:9로 추정 (모던 영상)
  if (lowerUrl.includes('fullscreen') || lowerUrl.includes('full-screen')) {
    reason = 'fullscreen 키워드'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
    return '16/9';
  }
  
  // 특정 IP나 도메인별 예외 처리
  if (lowerUrl.includes('222.103.41.1:8080')) {
    reason = '특정 서버 예외'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
    return '16/9';
  }
  
  // HTML 페이지 형태는 보통 16:9 (camera-fullscreen.html 등)
  if (lowerUrl.includes('.html') && lowerUrl.includes('camera')) {
    reason = 'HTML 카메라 페이지'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
    return '16/9';
  }
  
  // CCTV/보안카메라 전통적 비율들 (4:3)
  if (lowerUrl.includes('cctv') || lowerUrl.includes('security')) {
    reason = 'CCTV/보안 키워드'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '4/3', reason });
    return '4/3';
  }
  if (lowerUrl.includes('nvr') || lowerUrl.includes('dvr')) {
    reason = 'NVR/DVR 키워드'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '4/3', reason });
    return '4/3';
  }
  
  // 포트 번호로 추정 (우선순위 낮음)
  if (lowerUrl.includes(':8081')) {
    reason = '8081 포트'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
    return '16/9';
  }
  if (lowerUrl.includes(':8554')) {
    reason = '8554 포트 (RTSP)'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '4/3', reason });
    return '4/3';
  }
  if (lowerUrl.includes(':8080')) {
    // 8080 포트는 HTML 페이지인지 확인
    if (lowerUrl.includes('.html') || lowerUrl.includes('fullscreen')) {
      reason = '8080 포트 HTML/fullscreen'; 
      console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
      return '16/9';
    }
    reason = '8080 포트 일반 스트림'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '4/3', reason });
    return '4/3';
  }
  
  // camera 키워드가 있지만 위 조건에 해당하지 않으면 16:9 (현대적 카메라)
  if (lowerUrl.includes('camera') || lowerUrl.includes('cam')) {
    reason = 'camera 키워드'; 
    console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
    return '16/9';
  }
  
  // 기본값: 현대적인 HD 영상 비율
  reason = '기본값'; 
  console.log('[WebFrameRenderer] 비율 추정:', { url, ratio: '16/9', reason });
  return '16/9';
};

// iframe 내부의 실제 영상 비율을 동적으로 감지
const detectRealVideoAspectRatio = (iframe, callback) => {
  if (!iframe || !iframe.contentWindow) {
    console.log('[WebFrameRenderer] iframe 또는 contentWindow가 없음');
    return;
  }

  const checkVideo = () => {
    try {
      // CORS 정책 때문에 접근이 안 될 수 있음
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      if (!iframeDoc) {
        console.log('[WebFrameRenderer] iframe 문서에 접근할 수 없음 (CORS)');
        return false;
      }

      // video 엘리먼트 찾기
      const videos = iframeDoc.querySelectorAll('video');
      
      if (videos.length > 0) {
        const video = videos[0]; // 첫 번째 비디오 사용
        
        console.log('[WebFrameRenderer] 비디오 엘리먼트 발견:', {
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          readyState: video.readyState
        });

        // 비디오 메타데이터가 로드되었는지 확인
        if (video.videoWidth && video.videoHeight) {
          const aspectRatio = video.videoWidth / video.videoHeight;
          const ratioString = `${video.videoWidth}/${video.videoHeight}`;
          
          console.log('[WebFrameRenderer] 실제 영상 비율 감지됨:', {
            width: video.videoWidth,
            height: video.videoHeight,
            aspectRatio: aspectRatio.toFixed(3),
            ratioString: ratioString
          });
          
          callback(aspectRatio, ratioString);
          return true;
        } else if (video.readyState >= 1) {
          // 메타데이터가 아직 로드되지 않았다면 잠시 후 다시 시도
          console.log('[WebFrameRenderer] 비디오 메타데이터 로딩 중...');
          
          const onLoadedMetadata = () => {
            if (video.videoWidth && video.videoHeight) {
              const aspectRatio = video.videoWidth / video.videoHeight;
              const ratioString = `${video.videoWidth}/${video.videoHeight}`;
              
              console.log('[WebFrameRenderer] 메타데이터 로드 후 비율 감지:', {
                width: video.videoWidth,
                height: video.videoHeight,
                aspectRatio: aspectRatio.toFixed(3),
                ratioString: ratioString
              });
              
              callback(aspectRatio, ratioString);
            }
            video.removeEventListener('loadedmetadata', onLoadedMetadata);
          };
          
          video.addEventListener('loadedmetadata', onLoadedMetadata);
          return true;
        }
      } else {
        // video 엘리먼트가 없다면 다른 미디어 엘리먼트 확인
        const images = iframeDoc.querySelectorAll('img');
        const canvases = iframeDoc.querySelectorAll('canvas');
        
        console.log('[WebFrameRenderer] 비디오 엘리먼트 없음, 다른 미디어 확인:', {
          images: images.length,
          canvases: canvases.length
        });
      }
      
    } catch (error) {
      console.log('[WebFrameRenderer] iframe 내용 접근 실패 (CORS):', error.message);
      return false;
    }
    
    return false;
  };

  // 즉시 확인
  if (checkVideo()) {
    return;
  }

  // 1초 후 다시 확인 (페이지 로딩 시간 고려)
  setTimeout(() => {
    if (checkVideo()) {
      return;
    }
    
    // 3초 후 마지막 확인
    setTimeout(() => {
      checkVideo();
    }, 2000);
  }, 1000);
};

// 콘텐츠 타입별 최적 설정 반환
const getOptimalSettings = (contentType, imageAspectRatio = null, url = null, dynamicRatio = null) => {
  switch (contentType) {
    case 'image':
      return {
        aspectRatio: imageAspectRatio || '4/3', // 이미지 실제 비율 또는 기본값
        objectFit: 'contain',
        minHeight: '200px',
        showScrollbar: false
      };
    
    case 'video':
      // 1순위: 실제 감지된 영상 비율, 2순위: URL 기반 추정 비율
      const finalRatio = dynamicRatio || estimateVideoAspectRatio(url);
      
      console.log('[WebFrameRenderer] 영상 비율 결정:', {
        dynamicRatio,
        estimatedRatio: estimateVideoAspectRatio(url),
        finalRatio,
        source: dynamicRatio ? '실제 감지' : 'URL 추정'
      });
      
      return {
        aspectRatio: finalRatio, 
        objectFit: 'contain', // 영상 전체를 표시하여 잘림 방지
        minHeight: '300px', // 적절한 최소 높이
        showScrollbar: false
      };
    
    case 'webpage':
    default:
      return {
        aspectRatio: null, // 웹페이지는 고정 비율 없음
        objectFit: 'fill',
        minHeight: '400px',
        showScrollbar: true
      };
  }
};

const WebFrameRenderer = ({ chartData, error: propError, isEditMode, onChartClick }) => {
  const iframeRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(propError);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  
  // 동적 사이즈 조정을 위한 상태들
  const [imageAspectRatio, setImageAspectRatio] = useState(null);
  const [contentType, setContentType] = useState('webpage');
  const [dynamicAspectRatio, setDynamicAspectRatio] = useState(null); // 실제 영상 비율

  // URL 읽기 - 표준 위치(url) 우선, 호환성을 위해 이전 위치들도 확인
  let url = chartData?.url ||                           // 표준 위치
            chartData?.data?.url ||                     // 레거시: chart_options.data.url
            chartData?.content?.data?.url ||            // 레거시: data_source.content.data.url
            null;
  const isImage = isImageUrl(url);
  
  // 영상 URL 원본 그대로 사용 (파라미터 추가 제거)

  // 콘텐츠 타입 감지 및 설정
  useEffect(() => {
    if (url) {
      const detectedType = getContentType(url);
      setContentType(detectedType);
      console.log('[WebFrameRenderer] 감지된 콘텐츠 타입:', detectedType, 'URL:', url);
    }
  }, [url]);

  // URL 연결 가능성 사전 체크 (연결 거부 등 네트워크 에러 감지용)
  useEffect(() => {
    if (!url || isImage) return;
    
    let isMounted = true;
    const controller = new AbortController();

    const checkConnection = async () => {
      try {
        // 5초 타임아웃 설정
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        // no-cors 모드로 요청하여 네트워크 연결 가능 여부만 확인
        // (성공 시 opaque response 반환, 실패 시 네트워크 에러 발생)
        await fetch(url, {
          method: 'GET',
          mode: 'no-cors',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        // 연결 성공 시 별도 작업 없음 (iframe 로드 진행)
      } catch (err) {
        if (isMounted) {
          console.warn('[WebFrameRenderer] 연결 사전 체크 실패:', err);
          let msg = '서버에 연결할 수 없습니다.';
          if (err.name === 'AbortError') msg = '서버 응답 시간이 초과되었습니다.';
          
          setError(msg);
          setLoading(false);
        }
      }
    };

    checkConnection();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [url, isImage, reloadTrigger]);

  // 자동 사이즈 조정 활성화 여부 (options가 있을 때만)
  const autoSizeAdjust = chartData?.options?.autoSizeAdjust !== false; // 기본값: true
  
  // 콘텐츠 타입별 최적 설정 계산 (useMemo로 최적화)
  const optimalSettings = useMemo(() => {
    return autoSizeAdjust ? getOptimalSettings(contentType, imageAspectRatio, url, dynamicAspectRatio) : {};
  }, [autoSizeAdjust, contentType, imageAspectRatio, url, dynamicAspectRatio]);

  // 모든 useEffect는 early return 전에 위치해야 함
  useEffect(() => {
    setError(propError);
  }, [propError]);

  // iframe 로드 후 보안 정책 체크 (웹페이지인 경우만)
  useEffect(() => {
    if (!isImage && iframeRef.current && url) {
      // 일부 사이트는 X-Frame-Options으로 iframe 로드를 차단함
      if (url.includes('naver.com') || 
          url.includes('google.com') || 
          url.includes('facebook.com') ||
          url.includes('youtube.com')) {
        console.warn('[WebFrameRenderer] 이 사이트는 iframe 내 표시를 허용하지 않을 수 있습니다:', url);
      }
    }
  }, [chartData, isImage, url]);

  // 영상 타입인 경우 iframe 로드 후 실제 영상 비율 감지
  useEffect(() => {
    if (contentType === 'video' && iframeRef.current && url && !isImage) {
      console.log('[WebFrameRenderer] 영상 비율 감지 시작:', url);
      
      const iframe = iframeRef.current;
      
      // iframe 로드 완료를 기다린 후 비율 감지
      const onIframeLoad = () => {
        console.log('[WebFrameRenderer] iframe 로드 완료, 영상 감지 시작');
        
        detectRealVideoAspectRatio(iframe, (aspectRatio, ratioString) => {
          console.log('[WebFrameRenderer] 실제 영상 비율 설정:', {
            aspectRatio,
            ratioString,
            previousRatio: dynamicAspectRatio
          });
          
          setDynamicAspectRatio(ratioString);
        });
      };
      
      // iframe이 이미 로드되었다면 즉시 실행
      if (iframe.contentDocument || iframe.contentWindow) {
        onIframeLoad();
      } else {
        // 아직 로드되지 않았다면 로드 이벤트 대기
        iframe.addEventListener('load', onIframeLoad);
        
        return () => {
          iframe.removeEventListener('load', onIframeLoad);
        };
      }
    }
  }, [contentType, url, isImage]); // dynamicAspectRatio는 의존성에서 제외 (무한 루프 방지)

  // 차트 데이터 검증 (모든 Hook 호출 후에 위치)
  if (!chartData || !url) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
        backgroundColor: '#f8f9fa',
        color: '#dc3545',
        fontSize: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        borderRadius: '8px',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div>
          <strong>웹프레임 오류:</strong><br />
          URL이 제공되지 않았습니다.
        </div>
      </div>
    );
  }

  const { data, options = {} } = chartData;
  const { allow } = data;
  
  const {
    title,
    height = optimalSettings.minHeight || '600px',
    aspectRatio = optimalSettings.aspectRatio || '16/9',
    showBorder = true,
    borderColor = '#e0e0e0',
    borderRadius = '8px',
    sandbox,
    loading: loadingAttr = 'lazy',
    showTitle = true,
    objectFit = optimalSettings.objectFit || 'contain'
  } = options;

  // 투명/프레임 옵션 처리: 배터리 그리드와 동일한 동작
  const isTransparent = options.transparent === true;
  const containerShowFrame = options.showFrame !== undefined ? options.showFrame : true;
  const computedShowBorder = (showBorder !== undefined) ? showBorder : containerShowFrame;

  // 모든 변수 정의 후 디버깅 로그 추가
  console.log('[WebFrameRenderer] 자동 사이즈 조정 설정:', {
    autoSizeAdjust,
    contentType,
    url,
    optimalSettings,
    finalSettings: {
      aspectRatio: contentType === 'video' ? optimalSettings.aspectRatio : aspectRatio,
      objectFit,
      height: contentType === 'video' ? 'auto' : height,
      imageAspectRatio,
      estimatedVideoRatio: contentType === 'video' ? optimalSettings.aspectRatio : null
    }
  });

  // 이미지 로드 완료 핸들러 (비율 감지 포함)
  const handleImageLoadSuccess = (event) => {
    setLoading(false);
    
    // 이미지 실제 비율 계산 및 저장
    if (autoSizeAdjust && contentType === 'image' && event.target) {
      const { naturalWidth, naturalHeight } = event.target;
      if (naturalWidth && naturalHeight) {
        const ratio = naturalWidth / naturalHeight;
        const ratioString = `${naturalWidth}/${naturalHeight}`;
        setImageAspectRatio(ratioString);
        console.log('[WebFrameRenderer] 이미지 실제 비율 감지:', {
          width: naturalWidth,
          height: naturalHeight,
          ratio: ratio,
          ratioString: ratioString
        });
      }
    }
    
    console.log('[WebFrameRenderer] 이미지 로드 성공');
  };

  // 일반 로드 완료 핸들러 (iframe용)
  const handleLoadSuccess = () => {
    setLoading(false);
    console.log('[WebFrameRenderer] 컨텐츠 로드 성공:', contentType);
  };

  // 로드 에러 핸들러
  const handleLoadError = () => {
    setLoading(false);
    if (isImage) {
      setError('이미지를 불러올 수 없습니다. URL을 확인해주세요.');
      console.error('[WebFrameRenderer] 이미지 로드 실패:', url);
    } else {
      setError('데이터를 불러올 수 없습니다.');
      console.error('[WebFrameRenderer] iframe 로드 실패 - 보안 정책(X-Frame-Options)으로 인해 차단될 수 있습니다.');
    }
  };

  // 더블클릭 핸들러 - 항상 새 창으로 URL 열기
  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // 다시 시도 핸들러
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setReloadTrigger(prev => prev + 1);
  };

  return (
    <div 
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',  // 부모 컨테이너 크기에 맞춤
        display: 'flex',
        flexDirection: 'column',
        // chart_options.transparent=true인 경우 컨테이너 배경 투명 처리
        backgroundColor: isTransparent ? 'transparent' : '#ffffff',
        borderRadius: borderRadius,
        overflow: 'hidden',
        position: 'relative'
      }}
      onDoubleClick={handleDoubleClick}
    >
      {/* 컨텐츠 영역 - 콘텐츠 타입별 최적화 */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: contentType === 'video' ? 'auto' : '100%', // 영상은 자동 높이 (aspect-ratio에 맞춤)
        aspectRatio: contentType === 'video' ? optimalSettings.aspectRatio : 'auto', // 동적 비율 적용
        minHeight: contentType === 'video' ? (optimalSettings.minHeight || '300px') : '300px',
        maxHeight: contentType === 'video' ? '80vh' : 'none', // 화면 높이 제한
        margin: contentType === 'video' ? '0 auto' : '0',  // 영상은 중앙 정렬
        // 외부 showFrame과 연동: 명시적 showBorder가 없으면 showFrame 기본값을 따름
        border: computedShowBorder && contentType !== 'video' ? `1px solid ${borderColor}` : 'none', // 영상은 테두리 없음
        borderRadius: computedShowBorder && contentType !== 'video' ? '4px' : '0', // 영상은 radius 없음
        padding: contentType === 'video' ? '0' : undefined, // 영상은 패딩 없음
        overflow: 'hidden',
        // 투명 옵션이면 콘텐츠 영역도 투명 처리 (영상은 기본 검정 배경을 투명으로 변경)
        backgroundColor: isTransparent ? 'transparent' : (contentType === 'video' ? '#000000' : '#ffffff')
      }}>
        
        {/* 제목 및 컨트롤 오버레이 */}
        {showTitle && title && (
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            right: '12px',
            zIndex: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pointerEvents: 'none'  // 제목 영역은 클릭 이벤트 무시
          }}>
            <div style={{
              color: '#ffffff',
              padding: '4px 8px',
              fontSize: '16px',
              fontWeight: '600',
              textAlign: 'center',
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              {title}
            </div>
          </div>
        )}

        {/* 편집 모드 오버레이 - 클릭 이벤트 제거하여 DashboardComponentRenderer가 처리하도록 함 */}
        {isEditMode && (
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              cursor: 'move',
              zIndex: 15,
              pointerEvents: 'none'  // 클릭 이벤트를 부모로 전달
            }}
          />
        )}

        {/* 로딩 인디케이터 */}
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 10
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{
                fontSize: '14px',
                color: '#666',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                {isImage ? '이미지 로딩 중...' : '차트 로딩 중...'}
              </span>
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f8f9fa', // 흰색에 가까운 회색 배경
            color: '#5f6368', // 너무 붉지 않은 회색 텍스트
            fontSize: '14px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            padding: '20px',
            textAlign: 'center',
            zIndex: 10,
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>⚠️</div>
            <div style={{ fontWeight: '600', fontSize: '16px', color: '#3c4043' }}>
              {isImage ? '이미지 로드 실패' : '차트 로딩 실패'}
            </div>
            <div style={{ fontSize: '13px', maxWidth: '80%' }}>
              잠시 후 다시 시도해주세요.
            </div>
            <button
              onClick={handleRetry}
              style={{
                marginTop: '12px',
                padding: '6px 16px',
                backgroundColor: '#ffffff',
                border: '1px solid #dadce0',
                borderRadius: '100px',
                color: '#3c4043',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s, box-shadow 0.2s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                outline: 'none'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#f1f3f4'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#ffffff'}
            >
              다시 시도
            </button>
          </div>
        )}

        {/* 이미지 렌더링 */}
        {isImage ? (
          <img
            key={`img-${reloadTrigger}`}
            ref={imageRef}
            src={url}
            alt={title || 'Image'}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: objectFit, // contain, cover, fill, scale-down 등
              objectPosition: 'center',
              display: error ? 'none' : 'block',
              pointerEvents: isEditMode ? 'none' : 'auto'  // 편집 모드에서만 상호작용 비활성화
            }}
            onLoad={handleImageLoadSuccess}  // 이미지 전용 로드 핸들러 사용
            onError={handleLoadError}
            loading={loadingAttr}
          />
        ) : (
          /* iframe 렌더링 - 콘텐츠 타입별 최적화 */
          <iframe
            key={`iframe-${reloadTrigger}`}
            ref={iframeRef}
            src={url}
            title={title || 'Web Frame'}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              display: error ? 'none' : 'block',
              pointerEvents: isEditMode ? 'none' : 'auto',  // 편집 모드에서만 상호작용 비활성화
              overflow: 'hidden'
            }}
            allow={allow || "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"}
            sandbox={sandbox || "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"}
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen={true}
            loading={loadingAttr}
            onLoad={handleLoadSuccess}
            onError={handleLoadError}
          />
        )}
      </div>

      {/* CSS 애니메이션 */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default WebFrameRenderer;